/* eslint-disable consistent-return */

import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import { includes, values, pick, map } from 'lodash';

import requiredIf from '../../modules/schema-conditional-required';

export const ELEMENT_TYPES = {
  INPUT: 'input',
  SELECT: 'select',
  RADIO: 'radio',
  IMAGE: 'image',
  VIDEO: 'video',
  LINK: 'link',
  ICON: 'icon',
  TEXTAREA: 'textarea',
  IFRAME: 'iframe',
  IMAPS: 'imaps',
  POINTCLOUD: 'pointcloud',
  BUTTON: 'button',
  MULTIPLEIMAPS: 'multipleimaps',
  properties: {
    INPUT: {
      name: 'INPUT',
      value: 'input',
      humanValue: 'Input Field'
    },
    SELECT: {
      name: 'SELECT',
      value: 'select',
      humanValue: 'Dropdown Field'
    },
    RADIO: {
      name: 'RADIO',
      value: 'radio',
      humanValue: 'Checkbox Field'
    },
    IMAGE: {
      name: 'IMAGE',
      value: 'image',
      humanValue: 'Picture Field'
    },
    VIDEO: {
      name: 'VIDEO',
      value: 'video',
      humanValue: 'Video Field'
    },
    LINK: {
      name: 'LINK',
      value: 'link',
      humanValue: 'Link Field'
    },
    ICON: {
      name: 'ICON',
      value: 'icon',
      humanValue: 'Icon Field'
    },
    TEXTAREA: {
      name: 'TEXTAREA',
      value: 'textarea',
      humanValue: 'Text Area'
    },
    IFRAME: {
      name: 'IFRAME',
      value: 'iframe',
      humanValue: 'Iframe'
    },
    IMAPS: {
      name: 'IMAPS',
      value: 'imaps',
      humanValue: 'IMaps'
    },
    POINTCLOUD: {
      name: 'POINTCLOUD',
      value: 'pointcloud',
      humanValue: 'Point Cloud'
    },
    BUTTON: {
      name: 'BUTTON',
      value: 'button',
      humanValue: 'Button'
    },
    MULTIPLEIMAPS: {
      name: 'MULTIPLEIMAPS',
      value: 'multipleimaps',
      humanValue: 'Multiple Imaps'
    }
  }
};

export const CAPTION_TYPES = pick(ELEMENT_TYPES, [
  'IMAGE',
  'VIDEO',
  'LINK',
  'ICON',
  'IFRAME',
  'BUTTON'
]);

export const LABEL_TYPES = pick(ELEMENT_TYPES, ['INPUT', 'SELECT', 'RADIO', 'TEXTAREA']);

export const OPTION_LIST_TYPES = pick(ELEMENT_TYPES, ['SELECT', 'RADIO']);

const AnnotationTemplates = new Mongo.Collection('AnnotationTemplates');

AnnotationTemplates.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

AnnotationTemplates.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

const tagSchema = new SimpleSchema({
  id: {
    type: String,
    label: 'The id of the associated tag'
  },
  text: {
    type: String,
    label: 'The name of the associated tag'
  }
});

const mapTemplateSchema = new SimpleSchema({
  mapId: {
    type: String,
    label: 'The id of the map selected'
  },
  coordinates: {
    type: Array,
    label: 'The coordinates of the annotation',
    optional: true
  },
  'coordinates.$': {
    type: Number,
    label: 'The coordinates of the annotation'
  },
  zoom: {
    type: Number,
    label: 'The zoom for the map'
  },
  linkMap: {
    type: String,
    label: 'The url of shared map',
    optional: true
  },
  centerMode: {
    type: String,
    label: 'Center mode selected',
    optional: true
  },
  coordsCenter: {
    type: Array,
    label: 'The coordinates for center the map',
    optional: true
  },
  'coordsCenter.$': {
    type: Number,
    label: 'The coordinates for center the map'
  },
  heightMap: {
    type: String,
    label: 'The height of map',
    optional: true
  },
  imgMap: {
    type: String,
    label: 'The image of base map',
    optional: true
  }
});

const connectionsTemplateSchema = new SimpleSchema({
  server: {
    type: Object,
    label: 'The server of point cloud',
    blackbox: true
  },
  source: {
    type: Array,
    label: 'The source of point cloud'
  },
  'source.$': {
    type: Object,
    label: 'The source of point cloud',
    blackbox: true
  },
  connector: {
    type: Object,
    label: 'The connector of point cloud',
    blackbox: true,
    optional: true
  }
});

const elementSchema = new SimpleSchema({
  row: {
    type: Number,
    label: 'The row number this element is located in the layout',
    min: 1
  },
  column: {
    type: Number,
    label:
      'The column number this element is located inside the row. The total number of columns has to be a multiple of 12',
    min: 1,
    max: 12
  },
  order: {
    type: Number,
    label: 'The order this element should appear inside the column',
    min: 1
  },
  elementKey: {
    type: String,
    label: 'Unique identifier for the element, no spaces or accents'
  },
  elementType: {
    type: String,
    label: 'The type of the form control element that is going to be used',
    allowedValues: map(ELEMENT_TYPES.properties, 'value')
  },
  optionList: {
    type: Array,
    label: 'The list of choices to use in select and radio elements',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(OPTION_LIST_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  'optionList.$': {
    type: String,
    label: 'An item in the radio or select list'
  },
  dataType: {
    type: String,
    label: 'The type of data for the input box',
    allowedValues: ['text', 'number', 'date'],
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.INPUT,
        schemaInstance: this
      });
    }
  },
  labelName: {
    type: String,
    label: 'For input/select/radio this is the name of the label. Set by admin',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(LABEL_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  caption: {
    type: String,
    label:
      'For image/video/link/icon this is the text that goes along with the image/video/link/icon. Set by user',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(CAPTION_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  value: {
    type: String,
    label:
      'The value entered by the user for this element. For input/select/radio is a string. For image/video/link/icon/iframe is the URL associated with the element',
    optional: true
  },
  iconKey: {
    type: String,
    label: 'The font-awesome icon string for the element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.ICON,
        schemaInstance: this
      });
    }
  },
  height: {
    type: Number,
    label: 'The height in pixels for the element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.IFRAME,
        schemaInstance: this
      });
    }
  },
  map: {
    type: mapTemplateSchema,
    label: 'The map selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.IMAPS,
        schemaInstance: this
      });
    }
  },
  multipleMaps: {
    type: Array,
    label: 'Multiple maps selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.MULTIPLEIMAPS,
        schemaInstance: this
      });
    }
  },
  'multipleMaps.$': {
    type: mapTemplateSchema,
    label: 'Multiple maps selected'
  },
  connections: {
    type: connectionsTemplateSchema,
    label: 'Connections selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.POINTCLOUD,
        schemaInstance: this
      });
    }
  },
  hiperLink: {
    type: String,
    label: 'Hiperlink ofr image',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(CAPTION_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  }
});

AnnotationTemplates.schema = new SimpleSchema({
  owner: {
    type: String,
    label: 'The ID of the user this annotation template belongs to'
  },
  createdAt: {
    type: String,
    label: 'The date this annotation template was created in the database',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  updatedAt: {
    type: String,
    label: 'The date this annotation template was last updated',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  deleted: {
    type: String,
    label: 'The date the annotation template was deleted',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  name: {
    type: String,
    label: 'A name to identify the annotation template'
  },
  icon: {
    type: String,
    label: 'The font-awesome icon string for the annotation template'
  },
  tags: {
    type: Array,
    label: 'The tags associated to the annotation template'
  },
  'tags.$': {
    type: tagSchema,
    label: 'The tags associated to the annotation template'
  },
  elements: {
    type: Array,
    label:
      'This array constaints all the elements and thiri configuration and layout info for the template'
  },
  'elements.$': {
    type: elementSchema,
    label: 'An layout element in the template'
  }
});

AnnotationTemplates.attachSchema(AnnotationTemplates.schema);

export default AnnotationTemplates;
